// PrimeVue traz apenas o locale em inglês embutido. Como a UI deixa o usuário
// escolher o idioma em tempo de execução (store ui → vue-i18n), precisamos
// alimentar o PrimeVue com o mesmo idioma — senão o DatePicker (e menus de
// filtro, paginador, etc.) ficam em inglês mesmo com a interface em pt-BR.
//
// Cada objeto é o conjunto COMPLETO de chaves de locale do PrimeVue: o config
// do PrimeVue substitui o locale inteiro (não faz merge com o default), então
// trocar para um locale com menos chaves deixaria buracos. Mantemos ambos
// completos. Fonte dos valores: locale oficial do PrimeVue (default en) +
// projeto community primelocale (pt-BR).

import type { LocaleCode } from "@/plugins/i18n";
import type { PrimeVueLocaleOptions } from "@primevue/core/config";

const enUS: PrimeVueLocaleOptions = {
  startsWith: "Starts with",
  contains: "Contains",
  notContains: "Not contains",
  endsWith: "Ends with",
  equals: "Equals",
  notEquals: "Not equals",
  noFilter: "No Filter",
  lt: "Less than",
  lte: "Less than or equal to",
  gt: "Greater than",
  gte: "Greater than or equal to",
  dateIs: "Date is",
  dateIsNot: "Date is not",
  dateBefore: "Date is before",
  dateAfter: "Date is after",
  clear: "Clear",
  apply: "Apply",
  matchAll: "Match All",
  matchAny: "Match Any",
  addRule: "Add Rule",
  removeRule: "Remove Rule",
  accept: "Yes",
  reject: "No",
  choose: "Choose",
  upload: "Upload",
  cancel: "Cancel",
  completed: "Completed",
  pending: "Pending",
  fileSizeTypes: ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
  dayNames: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  monthNames: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ],
  monthNamesShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  chooseYear: "Choose Year",
  chooseMonth: "Choose Month",
  chooseDate: "Choose Date",
  prevDecade: "Previous Decade",
  nextDecade: "Next Decade",
  prevYear: "Previous Year",
  nextYear: "Next Year",
  prevMonth: "Previous Month",
  nextMonth: "Next Month",
  prevHour: "Previous Hour",
  nextHour: "Next Hour",
  prevMinute: "Previous Minute",
  nextMinute: "Next Minute",
  prevSecond: "Previous Second",
  nextSecond: "Next Second",
  am: "am",
  pm: "pm",
  today: "Today",
  weekHeader: "Wk",
  firstDayOfWeek: 0,
  showMonthAfterYear: false,
  dateFormat: "mm/dd/yy",
  weak: "Weak",
  medium: "Medium",
  strong: "Strong",
  passwordPrompt: "Enter a password",
  emptyFilterMessage: "No results found",
  searchMessage: "{0} results are available",
  selectionMessage: "{0} items selected",
  emptySelectionMessage: "No selected item",
  emptySearchMessage: "No results found",
  fileChosenMessage: "{0} files",
  noFileChosenMessage: "No file chosen",
  emptyMessage: "No available options",
  aria: {
    trueLabel: "True",
    falseLabel: "False",
    nullLabel: "Not Selected",
    star: "1 star",
    stars: "{star} stars",
    selectAll: "All items selected",
    unselectAll: "All items unselected",
    close: "Close",
    previous: "Previous",
    next: "Next",
    navigation: "Navigation",
    scrollTop: "Scroll Top",
    moveTop: "Move Top",
    moveUp: "Move Up",
    moveDown: "Move Down",
    moveBottom: "Move Bottom",
    moveToTarget: "Move to Target",
    moveToSource: "Move to Source",
    moveAllToTarget: "Move All to Target",
    moveAllToSource: "Move All to Source",
    pageLabel: "Page {page}",
    firstPageLabel: "First Page",
    lastPageLabel: "Last Page",
    nextPageLabel: "Next Page",
    prevPageLabel: "Previous Page",
    rowsPerPageLabel: "Rows per page",
    jumpToPageDropdownLabel: "Jump to Page Dropdown",
    jumpToPageInputLabel: "Jump to Page Input",
    selectRow: "Row Selected",
    unselectRow: "Row Unselected",
    expandRow: "Row Expanded",
    collapseRow: "Row Collapsed",
    showFilterMenu: "Show Filter Menu",
    hideFilterMenu: "Hide Filter Menu",
    filterOperator: "Filter Operator",
    filterConstraint: "Filter Constraint",
    editRow: "Row Edit",
    saveEdit: "Save Edit",
    cancelEdit: "Cancel Edit",
    listView: "List View",
    gridView: "Grid View",
    slide: "Slide",
    slideNumber: "{slideNumber}",
    zoomImage: "Zoom Image",
    zoomIn: "Zoom In",
    zoomOut: "Zoom Out",
    rotateRight: "Rotate Right",
    rotateLeft: "Rotate Left",
    listLabel: "Option List",
  },
};

const ptBR: PrimeVueLocaleOptions = {
  startsWith: "Começa com",
  contains: "Contém",
  notContains: "Não contém",
  endsWith: "Termina com",
  equals: "Igual",
  notEquals: "Diferente",
  noFilter: "Sem filtro",
  lt: "Menor que",
  lte: "Menor ou igual a",
  gt: "Maior que",
  gte: "Maior ou igual a",
  dateIs: "Data é",
  dateIsNot: "Data não é",
  dateBefore: "Data é anterior a",
  dateAfter: "Data é posterior a",
  clear: "Limpar",
  apply: "Aplicar",
  matchAll: "Corresponder a todos",
  matchAny: "Corresponder a qualquer",
  addRule: "Adicionar regra",
  removeRule: "Remover regra",
  accept: "Sim",
  reject: "Não",
  choose: "Escolher",
  upload: "Enviar",
  cancel: "Cancelar",
  completed: "Concluído",
  pending: "Pendente",
  fileSizeTypes: ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
  dayNames: ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"],
  dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
  dayNamesMin: ["D", "S", "T", "Q", "Q", "S", "S"],
  monthNames: [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ],
  monthNamesShort: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
  chooseYear: "Escolha o ano",
  chooseMonth: "Escolha o mês",
  chooseDate: "Escolha a data",
  prevDecade: "Década anterior",
  nextDecade: "Próxima década",
  prevYear: "Ano anterior",
  nextYear: "Próximo ano",
  prevMonth: "Mês anterior",
  nextMonth: "Próximo mês",
  prevHour: "Hora anterior",
  nextHour: "Próxima hora",
  prevMinute: "Minuto anterior",
  nextMinute: "Próximo minuto",
  prevSecond: "Segundo anterior",
  nextSecond: "Próximo segundo",
  am: "am",
  pm: "pm",
  today: "Hoje",
  weekHeader: "Sem",
  firstDayOfWeek: 0,
  showMonthAfterYear: false,
  dateFormat: "dd/mm/yy",
  weak: "Fraca",
  medium: "Média",
  strong: "Forte",
  passwordPrompt: "Digite uma senha",
  emptyFilterMessage: "Nenhum resultado encontrado",
  searchMessage: "{0} resultados disponíveis",
  selectionMessage: "{0} itens selecionados",
  emptySelectionMessage: "Nenhum item selecionado",
  emptySearchMessage: "Nenhum resultado encontrado",
  fileChosenMessage: "{0} arquivos",
  noFileChosenMessage: "Nenhum arquivo escolhido",
  emptyMessage: "Nenhuma opção disponível",
  aria: {
    trueLabel: "Verdadeiro",
    falseLabel: "Falso",
    nullLabel: "Não selecionado",
    star: "1 estrela",
    stars: "{star} estrelas",
    selectAll: "Todos os itens selecionados",
    unselectAll: "Nenhum item selecionado",
    close: "Fechar",
    previous: "Anterior",
    next: "Próximo",
    navigation: "Navegação",
    scrollTop: "Rolar para o topo",
    moveTop: "Mover para o topo",
    moveUp: "Mover para cima",
    moveDown: "Mover para baixo",
    moveBottom: "Mover para o final",
    moveToTarget: "Mover para o destino",
    moveToSource: "Mover para a origem",
    moveAllToTarget: "Mover todos para o destino",
    moveAllToSource: "Mover todos para a origem",
    pageLabel: "Página {page}",
    firstPageLabel: "Primeira página",
    lastPageLabel: "Última página",
    nextPageLabel: "Próxima página",
    prevPageLabel: "Página anterior",
    rowsPerPageLabel: "Linhas por página",
    jumpToPageDropdownLabel: "Ir para página (lista)",
    jumpToPageInputLabel: "Ir para página (campo)",
    selectRow: "Linha selecionada",
    unselectRow: "Linha desmarcada",
    expandRow: "Linha expandida",
    collapseRow: "Linha recolhida",
    showFilterMenu: "Mostrar menu de filtro",
    hideFilterMenu: "Ocultar menu de filtro",
    filterOperator: "Operador de filtro",
    filterConstraint: "Restrição de filtro",
    editRow: "Editar linha",
    saveEdit: "Salvar edição",
    cancelEdit: "Cancelar edição",
    listView: "Visão em lista",
    gridView: "Visão em grade",
    slide: "Slide",
    slideNumber: "{slideNumber}",
    zoomImage: "Ampliar imagem",
    zoomIn: "Aproximar",
    zoomOut: "Afastar",
    rotateRight: "Girar à direita",
    rotateLeft: "Girar à esquerda",
    listLabel: "Lista de opções",
  },
};

const BY_CODE: Record<LocaleCode, PrimeVueLocaleOptions> = {
  "pt-BR": ptBR,
  "en-US": enUS,
};

/** Conjunto de mensagens de locale do PrimeVue para o código de idioma dado. */
export function primeLocaleMessages(code: LocaleCode): PrimeVueLocaleOptions {
  return BY_CODE[code] ?? ptBR;
}
